import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Gamepad2, Star, Clock, Users, GraduationCap, Layers, Trophy, ChevronLeft, PackageCheck,
} from "lucide-react";

const LANGUAGE_DEPENDENCE_LABELS = [
  null,
  "No in-game text",
  "Some text needed",
  "Moderate text",
  "Extensive text",
  "Must know language",
];

const SUBTYPE_LABEL = {
  boardgameexpansion: "Expansion",
  boardgameaccessory: "Accessory",
};

function StatPill({ icon: Icon, label, value }) {
  if (value == null) return null;
  return (
    <div className="flex items-center gap-1.5 bg-muted rounded-lg px-3 py-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-[10px] text-muted-foreground leading-none mb-0.5">{label}</p>
        <p className="text-xs font-semibold text-foreground leading-none">{value}</p>
      </div>
    </div>
  );
}

function GameTile({ game, isBase, onClick, onWantToPlay, onBring, wantToPlayIds, bringIds, myOwnedIds }) {
  const id = game.bgg_id ?? game.bgg_game_id;
  const userOwns = myOwnedIds?.has(id);
  const bringing = bringIds?.has(id);
  const wanting = wantToPlayIds?.has(id);

  return (
    <div className="flex flex-col gap-1 w-24 shrink-0">
      <button
        type="button"
        onClick={onClick}
        className={`relative aspect-[2/3] w-24 rounded-md overflow-hidden bg-muted group ${
          isBase ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary transition-all"
        }`}
      >
        {game.thumbnail_url || game.image_url ? (
          <img
            src={game.thumbnail_url || game.image_url}
            alt={game.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gamepad2 className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}
        {game.wtp_count > 0 && (
          <div className="absolute bottom-1 right-1 z-10" title={`${game.wtp_count} ${game.wtp_count === 1 ? "person wants" : "people want"} to play this`}>
            <span className="inline-flex items-center gap-0.5 bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              <Star className="h-2 w-2" />
              {game.wtp_count}
            </span>
          </div>
        )}
        {isBase && (
          <div className="absolute bottom-0 inset-x-0 bg-primary/90 py-0.5 text-center">
            <span className="text-[8px] font-bold text-primary-foreground uppercase tracking-wide">Base</span>
          </div>
        )}
      </button>
      <p className="text-xs font-medium leading-snug line-clamp-2 text-foreground" title={game.title}>
        {game.title}
      </p>
      {!isBase && (userOwns ? onBring : onWantToPlay) && (
        userOwns ? (
          <button
            type="button"
            onClick={() => onBring(id)}
            className={`w-full py-0.5 rounded text-[10px] font-medium leading-none flex items-center justify-center gap-0.5 transition-all
              ${bringing
                ? "bg-green-500/20 text-green-700 dark:text-green-400 ring-1 ring-green-500/30"
                : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"}`}
          >
            <PackageCheck className="h-2.5 w-2.5 shrink-0" />
            {bringing ? "✓" : "Bring?"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onWantToPlay(id)}
            className={`w-full py-0.5 rounded text-[10px] font-medium leading-none flex items-center justify-center gap-0.5 transition-all
              ${wanting
                ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"}`}
          >
            <Star className="h-2.5 w-2.5 shrink-0" />
            {wanting ? "✓" : "Want"}
          </button>
        )
      )}
    </div>
  );
}

export default function GameDetailModal({ game, open, onClose, allGames = [], expansionsByParent = new Map(), onWantToPlay, wantToPlayIds = new Set(), onBring, bringIds = new Set(), myOwnedIds = new Set() }) {
  const [currentGame, setCurrentGame] = useState(game);
  const scrollRef = useRef(null);

  // Reset to the clicked game whenever the modal opens with a new game
  useEffect(() => {
    if (open) setCurrentGame(game);
  }, [game, open]);

  // Scroll to top whenever the displayed game changes
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentGame]);

  if (!currentGame) return null;

  const isExpansion = currentGame.subtype === "boardgameexpansion" || currentGame.subtype === "boardgameaccessory";

  // Expansions/accessories this base game has (from user's library)
  const ownedExpansions = isExpansion ? [] : (expansionsByParent.get(currentGame.bgg_id) ?? []);

  // For an expansion: find parent game (from library or from embedded parent_game field)
  const parentGame = isExpansion
    ? (allGames.find((g) => g.bgg_id === currentGame.parent_bgg_id) ?? currentGame.parent_game ?? null)
    : null;

  // Siblings: other expansions of the same parent that the user owns
  const siblings = isExpansion && currentGame.parent_bgg_id
    ? (expansionsByParent.get(currentGame.parent_bgg_id) ?? []).filter((g) => g.bgg_id !== currentGame.bgg_id)
    : [];

  const relatedGames = isExpansion
    ? [parentGame, ...siblings].filter(Boolean)
    : ownedExpansions;

  function navigateTo(targetGame) {
    // If navigating to parent game which may not be a full library entry,
    // prefer the full library entry if available
    const libraryEntry = allGames.find((g) => g.bgg_id === targetGame.bgg_id);
    setCurrentGame(libraryEntry ?? targetGame);
  }

  const playerRange =
    currentGame.min_players != null && currentGame.max_players != null
      ? currentGame.min_players === currentGame.max_players
        ? `${currentGame.min_players}`
        : `${currentGame.min_players}–${currentGame.max_players}`
      : null;

  const playtimeRange =
    currentGame.min_playtime != null && currentGame.max_playtime != null
      ? currentGame.min_playtime === currentGame.max_playtime
        ? `${currentGame.min_playtime} min`
        : `${currentGame.min_playtime}–${currentGame.max_playtime} min`
      : null;

  const bestWith = (() => {
    const counts = currentGame.best_with_counts;
    if (!counts?.length) return null;
    const lo = counts[0], hi = counts.at(-1);
    return lo === hi ? `${lo} players` : `${lo}–${hi} players`;
  })();

  const recPlayers =
    currentGame.rec_min_players != null && currentGame.rec_max_players != null
      ? currentGame.rec_min_players === currentGame.rec_max_players
        ? `${currentGame.rec_min_players}`
        : `${currentGame.rec_min_players}–${currentGame.rec_max_players}`
      : null;

  const langLabel = currentGame.language_dependence != null
    ? LANGUAGE_DEPENDENCE_LABELS[currentGame.language_dependence] ?? null
    : null;

  const subtypeLabel = SUBTYPE_LABEL[currentGame.subtype];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent ref={scrollRef} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-2 pr-6">
            {currentGame !== game && (
              <button
                type="button"
                onClick={() => setCurrentGame(game)}
                className="mt-1 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Back to original game"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-xl leading-snug">{currentGame.title}</DialogTitle>
                {subtypeLabel && (
                  <Badge variant="secondary" className="text-[10px] shrink-0">{subtypeLabel}</Badge>
                )}
              </div>
              {currentGame.year_published != null && (
                <p className="text-sm text-muted-foreground mt-0.5">{currentGame.year_published}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-5 mt-1">
          {/* Cover image */}
          <div className="shrink-0">
            {currentGame.image_url || currentGame.thumbnail_url ? (
              <img
                src={currentGame.image_url || currentGame.thumbnail_url}
                alt={currentGame.title}
                className="w-36 h-auto rounded-lg object-cover shadow-md"
              />
            ) : (
              <div className="w-36 h-48 rounded-lg bg-muted flex items-center justify-center">
                <Gamepad2 className="h-8 w-8 text-muted-foreground/40" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatPill icon={Users}        label="Players"       value={playerRange} />
              <StatPill icon={Clock}        label="Play time"     value={playtimeRange} />
              <StatPill icon={Star}         label="BGG Rating"    value={currentGame.bgg_rating != null ? Number(currentGame.bgg_rating).toFixed(1) : null} />
              <StatPill icon={Trophy}       label="BGG Rank"      value={currentGame.bgg_rank != null ? `#${currentGame.bgg_rank}` : null} />
              <StatPill icon={Layers}       label="Complexity"    value={currentGame.weight != null ? `${Number(currentGame.weight).toFixed(1)} / 5` : null} />
              <StatPill icon={Users}        label="Best with"     value={bestWith} />
              <StatPill icon={Users}        label="Recommended"   value={recPlayers != null ? `${recPlayers} players` : null} />
              <StatPill icon={GraduationCap} label="Min age"      value={currentGame.min_age != null ? `${currentGame.min_age}+` : null} />
              {langLabel && (
                <StatPill icon={GraduationCap} label="Language" value={langLabel} />
              )}
            </div>

            {/* Mechanics */}
            {currentGame.mechanics?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Mechanics
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {currentGame.mechanics.map((m) => (
                    <Badge key={m.id} variant="secondary" className="text-xs font-normal">
                      {m.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {currentGame.categories?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Categories
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {currentGame.categories.map((c) => (
                    <Badge key={c.id} variant="outline" className="text-xs font-normal">
                      {c.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {currentGame.description && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Description
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-[12]">
              {currentGame.description}
            </p>
          </div>
        )}

        {/* Expansions / Related games section */}
        {relatedGames.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {isExpansion ? "Part of this series" : `Expansions & Accessories (${relatedGames.length})`}
            </p>
            <div className="flex flex-wrap gap-3">
              {relatedGames.map((g) => (
                <GameTile
                  key={g.bgg_id}
                  game={g}
                  isBase={isExpansion && g.bgg_id === currentGame.parent_bgg_id}
                  onClick={() => navigateTo(g)}
                  onWantToPlay={onWantToPlay}
                  onBring={onBring}
                  wantToPlayIds={wantToPlayIds}
                  bringIds={bringIds}
                  myOwnedIds={myOwnedIds}
                />
              ))}
            </div>
          </div>
        )}

        {/* BGG link */}
        <div className="mt-4 pt-3 border-t border-border">
          <a
            href={`https://boardgamegeek.com/boardgame/${currentGame.bgg_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View on BoardGameGeek →
          </a>
        </div>

        {/* Action button */}
        {(onWantToPlay || onBring) && (() => {
          const id = currentGame.bgg_id;
          const userOwns = myOwnedIds.has(id) || bringIds.has(id);
          if (userOwns && onBring) {
            const bringing = bringIds.has(id);
            return (
              <div className="mt-3 pt-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => onBring(id)}
                  className={`w-full py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all
                    ${bringing
                      ? "bg-green-500/20 text-green-700 dark:text-green-400 ring-1 ring-green-500/30"
                      : "bg-muted hover:bg-muted/80 text-foreground"}`}
                >
                  <PackageCheck className="h-4 w-4 shrink-0" />
                  {bringing ? "Bringing ✓" : "Mark as Bringing"}
                </button>
              </div>
            );
          }
          if (onWantToPlay) {
            const wanting = wantToPlayIds.has(id);
            return (
              <div className="mt-3 pt-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => onWantToPlay(id)}
                  className={`w-full py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all
                    ${wanting
                      ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                      : "bg-muted hover:bg-muted/80 text-foreground"}`}
                >
                  <Star className="h-4 w-4 shrink-0" />
                  {wanting ? "Want to Play ✓" : "Want to Play"}
                </button>
              </div>
            );
          }
          return null;
        })()}
      </DialogContent>
    </Dialog>
  );
}
